import React, { useState, useEffect, useRef } from 'react';
import type { AxiosError } from 'axios';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { login, clearError } from '../store/slices/authSlice';
import { clearStoredSessionNotice, getStoredSessionNotice } from '../api/client';
import { verifyMFAChallenge } from '../api/mfa';
import { lookupSsoDomain, getSsoInitiateUrl } from '../api/sso';
import Input from '../components/common/Input';
import PasswordInput from '../components/common/PasswordInput';
import Button from '../components/common/Button';
import InlineStatus from '../components/common/InlineStatus';
import useSeoMeta from '../hooks/useSeoMeta';
import posthog from 'posthog-js';
import { trackUserLoggedIn } from '../lib/analytics';
import type { AuthResponse } from '../types';
import {
  clearAuthReturnContext,
  getAuthReturnMeta,
  getPostAuthReturnPath,
  syncAuthReturnContextFromSearch,
} from '../lib/authReturnContext';

type LoginStepId = 'email' | 'password';

type LoginMfaPayload = Pick<AuthResponse, 'requiresMFA' | 'mfaToken'> & {
  requiresMFA: true;
  mfaToken: string;
};

interface ApiErrorResponse {
  error?: string;
}

const isLoginMfaPayload = (payload: unknown): payload is LoginMfaPayload => {
  if (typeof payload !== 'object' || payload === null) {
    return false;
  }

  return (
    'requiresMFA' in payload &&
    payload.requiresMFA === true &&
    'mfaToken' in payload &&
    typeof payload.mfaToken === 'string'
  );
};

const getApiErrorMessage = (error: unknown, fallback: string): string => {
  const apiError = error as AxiosError<ApiErrorResponse>;

  return apiError.response?.data?.error ?? fallback;
};

const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [validationError, setValidationError] = useState('');
  // MFA challenge state
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaToken, setMfaToken] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [mfaLoading, setMfaLoading] = useState(false);
  const [mfaError, setMfaError] = useState<string | null>(null);
  // SSO modal state
  const [ssoModalOpen, setSsoModalOpen] = useState(false);
  const [ssoDomain, setSsoDomain] = useState('');
  const [ssoLoading, setSsoLoading] = useState(false);
  const [ssoError, setSsoError] = useState<string | null>(null);
  const [sessionNotice, setSessionNotice] = useState<string | null>(() => getStoredSessionNotice());
  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, isLoading, error } = useAppSelector(
    (state) => state.auth
  );
  const trimmedEmail = email.trim();
  const hasValidEmail = /\S+@\S+\.\S+/.test(trimmedEmail);
  const loginSteps: Array<{ id: LoginStepId; label: string; complete: boolean }> = [
    { id: 'email', label: 'Use a valid email address', complete: hasValidEmail },
    { id: 'password', label: 'Enter your password', complete: password.length > 0 },
  ];
  const completedSteps = loginSteps.filter((step) => step.complete).length;
  const missingRequirements = loginSteps
    .filter((step) => !step.complete)
    .map((step) => step.label);
  const firstIncompleteStep = loginSteps.find((step) => !step.complete) ?? null;
  const canSubmit = missingRequirements.length === 0;
  const progressPercent = (completedSteps / loginSteps.length) * 100;
  const [authReturnContext, setAuthReturnContext] = useState(() =>
    syncAuthReturnContextFromSearch(location.search)
  );
  const authReturnMeta = React.useMemo(
    () => getAuthReturnMeta(authReturnContext),
    [authReturnContext]
  );

  useSeoMeta('Sign In — Pinecurl', 'Sign in to your Pinecurl account to manage your crypto portfolio.');

  useEffect(() => {
    setAuthReturnContext(syncAuthReturnContextFromSearch(location.search));
  }, [location.search]);

  useEffect(() => {
    if (isAuthenticated && localStorage.getItem('accessToken')) {
      const nextPath = getPostAuthReturnPath('/dashboard');
      clearAuthReturnContext();
      navigate(nextPath, { replace: true });
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    return () => {
      dispatch(clearError());
    };
  }, [dispatch]);

  useEffect(() => {
    if (isAuthenticated || mfaRequired || ssoModalOpen) {
      return;
    }

    emailRef.current?.focus();
  }, [isAuthenticated, mfaRequired, ssoModalOpen]);

  const getCredentialSnapshot = React.useCallback(() => {
    const nextEmail = emailRef.current?.value ?? email;
    const nextPassword = passwordRef.current?.value ?? password;
    const nextTrimmedEmail = nextEmail.trim();

    return {
      email: nextEmail,
      password: nextPassword,
      trimmedEmail: nextTrimmedEmail,
      hasValidEmail: /\S+@\S+\.\S+/.test(nextTrimmedEmail),
    };
  }, [email, password]);

  const syncCredentialsFromInputs = React.useCallback(() => {
    const snapshot = getCredentialSnapshot();

    setEmail((current) => (current === snapshot.email ? current : snapshot.email));
    setPassword((current) => (current === snapshot.password ? current : snapshot.password));

    return snapshot;
  }, [getCredentialSnapshot]);

  useEffect(() => {
    if (isAuthenticated || mfaRequired || ssoModalOpen) {
      return;
    }

    let syncAttempts = 0;

    const syncAndMaybeStop = () => {
      syncCredentialsFromInputs();
      syncAttempts += 1;

      const hasSavedCredentials =
        Boolean(emailRef.current?.value) && Boolean(passwordRef.current?.value);

      if (hasSavedCredentials || syncAttempts >= 8) {
        window.clearInterval(intervalId);
      }
    };

    const handleWindowFocus = () => {
      syncCredentialsFromInputs();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        syncCredentialsFromInputs();
      }
    };

    syncCredentialsFromInputs();

    const frameId = window.requestAnimationFrame(syncAndMaybeStop);
    const intervalId = window.setInterval(syncAndMaybeStop, 250);

    window.addEventListener('focus', handleWindowFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.cancelAnimationFrame(frameId);
      window.clearInterval(intervalId);
      window.removeEventListener('focus', handleWindowFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isAuthenticated, mfaRequired, ssoModalOpen, syncCredentialsFromInputs]);

  const focusStep = (stepId: LoginStepId) => {
    const target = stepId === 'email' ? emailRef.current : passwordRef.current;

    if (!target) {
      return;
    }

    if (typeof target.scrollIntoView === 'function') {
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    target.focus({ preventScroll: true });
  };

  const resetLoginFeedback = () => {
    setValidationError('');
    setMfaError(null);

    if (error) {
      dispatch(clearError());
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    resetLoginFeedback();

    const latestCredentials = syncCredentialsFromInputs();

    if (!latestCredentials.hasValidEmail) {
      setValidationError('Enter a valid email address');
      focusStep('email');
      return;
    }

    if (latestCredentials.password.length === 0) {
      setValidationError('Enter your password');
      focusStep('password');
      return;
    }

    const result = await dispatch(
      login({
        email: latestCredentials.trimmedEmail,
        password: latestCredentials.password,
      })
    );
    if (login.fulfilled.match(result)) {
      clearStoredSessionNotice();
      setSessionNotice(null);
      const { user } = result.payload;

      posthog.identify(user.id, {
        email: user.email,
        name: user.displayName || user.email,
      });
      trackUserLoggedIn(user.id, 'email');
    }
    // Check if the response requires MFA (login thunk will reject with requiresMFA)
    if (login.rejected.match(result) && isLoginMfaPayload(result.payload)) {
      setMfaRequired(true);
      setMfaToken(result.payload.mfaToken);
    }
  };

  const handleMFASubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (totpCode.length !== 6) {
      setMfaError('Enter the 6-digit code from your authenticator app');
      return;
    }
    setMfaLoading(true);
    setMfaError(null);
    try {
      const response = await verifyMFAChallenge(mfaToken, totpCode);
      clearStoredSessionNotice();
      setSessionNotice(null);
      localStorage.setItem('accessToken', response.accessToken);
      localStorage.setItem('refreshToken', response.refreshToken);
      const nextPath = getPostAuthReturnPath('/dashboard');
      clearAuthReturnContext();
      navigate(nextPath, { replace: true });
    } catch (error: unknown) {
      setMfaError(getApiErrorMessage(error, 'Invalid code. Please try again.'));
      setTotpCode('');
    } finally {
      setMfaLoading(false);
    }
  };

  const handleSsoSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const domain = ssoDomain.trim().toLowerCase();
    if (!domain) {
      setSsoError('Please enter your organisation domain.');
      return;
    }
    setSsoLoading(true);
    setSsoError(null);
    try {
      const { orgId } = await lookupSsoDomain(domain);
      // Navigate browser to initiate SSO flow (redirect chain to IdP)
      window.location.href = getSsoInitiateUrl(orgId);
    } catch {
      setSsoError('No SSO configuration found for this domain. Check the domain or use email/password.');
    } finally {
      setSsoLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-app px-4">
      {/* SSO Domain Modal */}
      {ssoModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
          <div className="bg-surface border border-border rounded-xl p-6 w-full max-w-sm shadow-xl">
            <h2 className="text-lg font-semibold text-text-primary mb-1">Sign in with SSO</h2>
            <p className="text-sm text-text-secondary mb-4">
              Enter your organisation's domain to be redirected to your identity provider.
            </p>
            <form onSubmit={handleSsoSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-1">
                  Organisation Domain
                </label>
                <input
                  type="text"
                  value={ssoDomain}
                  onChange={(e) => { setSsoDomain(e.target.value); setSsoError(null); }}
                  placeholder="acme.com"
                  autoFocus
                  className="w-full px-3 py-2.5 bg-surface-raised border border-border rounded-lg text-sm text-text-primary placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500"
                />
              </div>
              {ssoError && (
                <p className="text-xs text-accent-red">{ssoError}</p>
              )}
              <div className="flex gap-2">
                <button
                  type="submit"
                  disabled={ssoLoading}
                  className="flex-1 py-2.5 bg-primary-600 hover:bg-primary-500 disabled:opacity-60 text-white text-sm font-medium rounded-lg transition-colors"
                >
                  {ssoLoading ? 'Looking up…' : authReturnMeta ? authReturnMeta.ssoButtonLabel : 'Continue with SSO'}
                </button>
                <button
                  type="button"
                  onClick={() => { setSsoModalOpen(false); setSsoError(null); setSsoDomain(''); }}
                  className="px-4 py-2.5 border border-border bg-surface-raised hover:border-primary-500/50 text-text-secondary text-sm rounded-lg transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="max-w-md w-full">
        <div className="mb-4">
          <Link to="/" className="text-gray-400 hover:text-white text-sm flex items-center gap-1 transition-colors">
            ← Back to home
          </Link>
        </div>
        <div className="bg-surface border border-border rounded-xl p-8 shadow-lg">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-text-primary">
              <span className="flex items-center justify-center gap-2">
                <img src="/hedgehog-logo.png" alt="" className="w-10 h-10" />
                Pinecurl
                <span className="text-[10px] font-semibold bg-purple-600 text-white px-1.5 py-0.5 rounded uppercase tracking-wide">Beta</span>
              </span>
            </h1>
            <p className="text-text-secondary text-sm mt-1">
              {mfaRequired ? 'Two-Factor Authentication' : 'Sign in to your portfolio'}
            </p>
          </div>

          {/* MFA Challenge Step */}
          {mfaRequired ? (
            <div>
              <div className="bg-primary-500/10 border border-primary-500/20 rounded-lg px-4 py-3 mb-5 text-sm text-primary-300">
                🔐 Your account has two-factor authentication enabled. Enter the 6-digit code from your authenticator app.
              </div>
              {authReturnMeta ? (
                <InlineStatus
                  tone="info"
                  title={`Resume ${authReturnMeta.routeName}`}
                  message={
                    authReturnMeta.proofLabel
                      ? `${authReturnMeta.proofLabel} remains the proof-backed task waiting on ${authReturnMeta.routeLabel}.`
                      : `Finish verification to return to ${authReturnMeta.routeLabel} without dropping the current proof-backed task.`
                  }
                  meta={
                    <>
                      <span>
                        Route:{' '}
                        <span className="font-mono tabular-nums text-gray-200">
                          {authReturnMeta.routeLabel}
                        </span>
                      </span>
                      <span>
                        Next step:{' '}
                        <span className="font-mono tabular-nums text-gray-200">
                          {authReturnMeta.ownerCtaLabel}
                        </span>
                      </span>
                    </>
                  }
                  className="mb-5 rounded-xl"
                />
              ) : null}
              {mfaError && (
                <div className="bg-red-500/10 border border-red-500/30 text-accent-red px-4 py-3 rounded-lg mb-4 text-sm">
                  {mfaError}
                </div>
              )}

              <form onSubmit={handleMFASubmit}>
                <div className="mb-5">
                  <label className="block text-sm font-medium text-text-secondary mb-2">
                    Authenticator Code
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={6}
                    placeholder="000000"
                    value={totpCode}
                    onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    autoFocus
                    className="w-full px-4 py-3 bg-surface-raised border border-border rounded-lg text-center text-2xl font-mono tracking-widest text-text-primary focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500"
                  />
                  <p className="text-xs text-text-muted mt-2 text-center">
                    You can also enter a backup code if you don't have access to your authenticator
                  </p>
                </div>
                <Button
                  type="submit"
                  className="w-full"
                  isLoading={mfaLoading}
                >
                  {authReturnMeta ? authReturnMeta.mfaButtonLabel : 'Verify'}
                </Button>
              </form>

              <button
                onClick={() => { setMfaRequired(false); setMfaToken(''); setTotpCode(''); setMfaError(null); }}
                className="w-full text-center text-xs text-text-muted hover:text-text-primary mt-4 transition-colors"
              >
                ← Back to login
              </button>
            </div>
          ) : (
            /* Regular Login Step */
            <>
              {sessionNotice && (
                <div className="bg-red-500/10 border border-red-500/30 text-red-200 px-4 py-3 rounded-lg mb-4 text-sm">
                  <div className="flex items-start justify-between gap-3">
                    <span>{sessionNotice}</span>
                    <button
                      type="button"
                      onClick={() => {
                        clearStoredSessionNotice();
                        setSessionNotice(null);
                      }}
                      className="text-red-300 hover:text-red-100 transition-colors"
                      aria-label="Dismiss session notice"
                    >
                      ✕
                    </button>
                  </div>
                </div>
              )}

              {authReturnMeta ? (
                <InlineStatus
                  tone="info"
                  title={`Resume ${authReturnMeta.routeName}`}
                  message={
                    authReturnMeta.proofLabel
                      ? `${authReturnMeta.proofLabel} remains mounted on ${authReturnMeta.routeLabel}. Sign in again to resume the same proof-backed task.`
                      : `Sign in again to return to ${authReturnMeta.routeLabel} and keep the same proof-backed task in place.`
                  }
                  meta={
                    <>
                      <span>
                        Route:{' '}
                        <span className="font-mono tabular-nums text-gray-200">
                          {authReturnMeta.routeLabel}
                        </span>
                      </span>
                      <span>
                        Next step:{' '}
                        <span className="font-mono tabular-nums text-gray-200">
                          {authReturnMeta.ownerCtaLabel}
                        </span>
                      </span>
                    </>
                  }
                  className="mb-6 rounded-xl"
                />
              ) : null}

              {(error || validationError) && (
                <div className="bg-red-500/10 border border-red-500/30 text-accent-red px-4 py-3 rounded-lg mb-6 text-sm">
                  {error || validationError}
                </div>
              )}

              <form onSubmit={handleSubmit} noValidate>
                <Input
                  ref={emailRef}
                  label="Email"
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    resetLoginFeedback();
                  }}
                  placeholder="you@example.com"
                  autoComplete="username"
                  required
                />
                <PasswordInput
                  ref={passwordRef}
                  label="Password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    resetLoginFeedback();
                  }}
                  placeholder="••••••••"
                  required
                  autoComplete="current-password"
                />

                <div className="mt-2 mb-4 rounded-lg border border-border bg-surface-raised/60 px-4 py-3">
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-sm font-medium text-text-primary">
                      Finish sign-in
                    </p>
                    <span className="text-xs text-text-muted">
                      {completedSteps}/{loginSteps.length} ready
                    </span>
                  </div>
                  <div className="mt-3 h-2 overflow-hidden rounded-full bg-app">
                    <div
                      className="h-full rounded-full bg-primary-500 transition-all"
                      style={{ width: `${progressPercent}%` }}
                    />
                  </div>
                  <ul className="mt-3 space-y-2 text-sm">
                    {loginSteps.map((step) => (
                      <li
                        key={step.id}
                        className="flex items-center justify-between gap-3 text-text-secondary"
                      >
                        <span>{step.label}</span>
                        {step.complete ? (
                          <span className="rounded-full bg-primary-500/15 px-2 py-0.5 text-[11px] font-medium uppercase tracking-wide text-primary-300">
                            Ready
                          </span>
                        ) : step.id === firstIncompleteStep?.id ? (
                          <button
                            type="button"
                            onClick={() => focusStep(step.id)}
                            className="rounded-full border border-primary-500/30 bg-primary-500/10 px-2 py-0.5 text-[11px] font-medium uppercase tracking-wide text-primary-300 transition-colors hover:border-primary-400/50 hover:bg-primary-500/15 focus:outline-none focus:ring-2 focus:ring-primary-500/40"
                            aria-label={`Continue to ${step.label.toLowerCase()}`}
                          >
                            Continue
                          </button>
                        ) : (
                          <span className="rounded-full bg-surface px-2 py-0.5 text-[11px] font-medium uppercase tracking-wide text-text-muted">
                            Pending
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                  {!canSubmit && firstIncompleteStep ? (
                    <p className="mt-3 text-xs text-text-muted">
                      Use <span className="text-text-primary">Continue</span> to jump to the next required step.
                    </p>
                  ) : null}
                </div>

                <p className="text-xs text-text-muted mb-4" aria-live="polite">
                  {canSubmit
                    ? 'Everything is ready. You can sign in now.'
                    : `Still needed: ${missingRequirements[0]}. Select Sign In or use Continue in the checklist to jump there.`}
                </p>

                <div className="text-right -mt-2 mb-4">
                  <Link
                    to="/password-reset"
                    className="text-xs text-text-muted hover:text-primary-400 transition-colors"
                  >
                    Forgot password?
                  </Link>
                </div>

                <Button
                  type="submit"
                  className="w-full"
                  isLoading={isLoading}
                >
                  {authReturnMeta ? authReturnMeta.loginButtonLabel : 'Sign In'}
                </Button>
              </form>

              <p className="text-center text-text-secondary text-sm mt-6">
                Don't have an account?{' '}
                <Link to="/register" className="text-primary-400 hover:text-primary-300">
                  Register
                </Link>
              </p>

              {/* SSO CTA */}
              <div className="mt-4 pt-4 border-t border-border">
                <button
                  type="button"
                  onClick={() => setSsoModalOpen(true)}
                  className="w-full text-center text-sm text-text-muted hover:text-primary-400 transition-colors py-1"
                >
                  🏢 {authReturnMeta ? authReturnMeta.ssoButtonLabel : 'Sign in with SSO'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
